import React from 'react';
import { Building2, Grape, Wine, ClipboardList, GitMerge, Package, Truck } from 'lucide-react';

const WorkflowNavigation = ({ onNavigate }) => {
  // Define workflow steps
  const workflowSteps = [
    { id: 'companies', icon: Building2, label: 'Companies', path: '/companies', color: '#6366F1' },
    { id: 'planning', icon: Grape, label: 'Plan', path: '/harvest-planning', color: '#10B981' },
    { id: 'wines', icon: Wine, label: 'Wines', path: '/wines', color: '#8B5CF6' },
    { id: 'workorders', icon: ClipboardList, label: 'Work Orders', path: '/workorders', color: '#F59E0B' },
    { id: 'blends', icon: GitMerge, label: 'Blends', path: '/blends', color: '#EC4899' },
    { id: 'bottling', icon: Package, label: 'Bottling', path: '/bottling-plans', color: '#06B6D4' },
    { id: 'cased-goods', icon: Package, label: 'Cased Goods', path: '/bottled-wines', color: '#3B82F6' },
    { id: 'shipping', icon: Truck, label: 'Shipping', path: '/bill-of-lading-list', color: '#EF4444' }
  ];

  // Split steps for two-row layout
  const firstRowSteps = workflowSteps.slice(0, 4);
  const secondRowSteps = workflowSteps.slice(4);

  return (
    <div className="card mb-4">
      <div className="card-header bg-white">
        <h5 className="card-title mb-0">Winery Workflow</h5>
      </div>
      <div className="card-body p-4">
        {/* Single row for large screens, visible at md and above */}
        <div className="d-none d-md-block">
          <div className="workflow-container">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  {/* Connection line between steps */}
                  {index > 0 && (
                    <div className="workflow-connector" />
                  )}
                  
                  {/* Step node */}
                  <div 
                    className="workflow-step"
                    onClick={() => onNavigate(step.path)}
                    style={{ 
                      backgroundColor: `${step.color}10`, 
                      borderColor: step.color 
                    }}
                  >
                    <div className="workflow-icon" style={{ backgroundColor: step.color }}>
                      <Icon color="white" size={20} />
                    </div>
                    <div className="workflow-label d-none d-sm-block">{step.label}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Two rows for smaller screens, visible below md */}
        <div className="d-md-none">
          {/* First Row */}
          <div className="workflow-container mb-3">
            {firstRowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  {/* Connection line between steps */}
                  {index > 0 && (
                    <div className="workflow-connector" />
                  )}
                  
                  {/* Step node */}
                  <div 
                    className="workflow-step"
                    onClick={() => onNavigate(step.path)}
                    style={{ 
                      backgroundColor: `${step.color}10`, 
                      borderColor: step.color 
                    }}
                  >
                    <div className="workflow-icon" style={{ backgroundColor: step.color }}>
                      <Icon color="white" size={20} />
                    </div>
                    <div className="workflow-label d-none d-sm-block">{step.label}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          
          {/* Second Row with downward connector from first to second row */}
          <div className="workflow-container">
            {secondRowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  {/* Connection line between steps */}
                  {index > 0 && (
                    <div className="workflow-connector" />
                  )}
                  
                  {/* Step node */}
                  <div 
                    className="workflow-step"
                    onClick={() => onNavigate(step.path)}
                    style={{ 
                      backgroundColor: `${step.color}10`, 
                      borderColor: step.color 
                    }}
                  >
                    <div className="workflow-icon" style={{ backgroundColor: step.color }}>
                      <Icon color="white" size={20} />
                    </div>
                    <div className="workflow-label d-none d-sm-block">{step.label}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          
          {/* Connector from first to second row */}
          <div className="workflow-vertical-connector">
            <div className="workflow-vertical-line"></div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .workflow-container {
          display: flex;
          align-items: center;
          flex-wrap: nowrap;
          overflow-x: auto;
          padding: 0.5rem;
          gap: 0.5rem;
          -ms-overflow-style: none;
          scrollbar-width: none;
          position: relative;
        }
        
        .workflow-container::-webkit-scrollbar {
          display: none;
        }
        
        .workflow-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 60px;
          padding: 0.5rem;
          border: 2px solid transparent;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          flex: 1;
        }
        
        .workflow-step:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .workflow-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .workflow-label {
          font-size: 0.75rem;
          font-weight: 500;
          text-align: center;
        }
        
        .workflow-connector {
          height: 2px;
          background-color: #e5e7eb;
          flex-grow: 1;
          min-width: 1rem;
          max-width: 2rem;
          position: relative;
        }
        
        .workflow-connector:after {
          content: '';
          position: absolute;
          right: 0;
          top: -3px;
          width: 8px;
          height: 8px;
          border-top: 2px solid #e5e7eb;
          border-right: 2px solid #e5e7eb;
          transform: rotate(45deg);
        }
        
        .workflow-vertical-connector {
          position: relative;
          height: 20px;
          display: flex;
          justify-content: center;
          margin-top: -30px;
          margin-bottom: -15px;
          z-index: 1;
        }
        
        .workflow-vertical-line {
          width: 2px;
          height: 100%;
          background-color: #e5e7eb;
          position: relative;
        }
        
        .workflow-vertical-line:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: -3px;
          width: 8px;
          height: 8px;
          border-bottom: 2px solid #e5e7eb;
          border-right: 2px solid #e5e7eb;
          transform: rotate(45deg);
        }
        
        @media (max-width: 767px) {
          .workflow-step {
            min-width: 60px;
            padding: 0.5rem;
          }
          .workflow-icon {
            width: 36px;
            height: 36px;
          }
        }
        
        @media (max-width: 575px) {
          .workflow-step {
            min-width: 50px;
            padding: 0.5rem 0.25rem;
          }
          .workflow-icon {
            width: 32px;
            height: 32px;
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default WorkflowNavigation;